import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { PageHeader } from "@/components/layout/PageHeader";
import { toast } from "sonner";
import { format } from "date-fns";
import { formatTimeRange } from "@/lib/format/date";
import { cn } from "@/lib/utils";
import { MapPin, Calendar as CalendarIcon, Clock, Check, ChevronRight, ChevronLeft, Plus, Loader2 } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

// Optional API base. In dev with Vite proxy, keep VITE_API_BASE empty.
const API_BASE = (import.meta as any)?.env?.VITE_API_BASE || "";
function buildUrl(path: string) {
  if (!API_BASE) return path;
  const base = API_BASE.replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

interface Address {
  id: number | string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  country?: string;
  pincode: string;
  isDefault?: boolean;
}

interface Slot {
  id: number | string;
  startAt: string;
  endAt: string;
  capacity: number;
  bookedCount: number;
  capacityLeft: number;
  state?: string;
  city?: string;
}

interface City {
  id: string;
  name: string;
}

const INDIAN_STATES: string[] = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand",
  "Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab",
  "Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu","Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry"
];

const STEPS = [
  { id: 1, title: "Address" },
  { id: 2, title: "Date" },
  { id: 3, title: "Time Slot" },
  { id: 4, title: "Confirm" },
];

export default function BookMeasurementPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [addresses, setAddresses] = useState<Address[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  const [showNewAddress, setShowNewAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    country: "India",
    pincode: "",
  });
  const [creatingAddress, setCreatingAddress] = useState(false);

  const [cities, setCities] = useState<City[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedAddress && selectedDate) {
      fetchSlots();
    }
  }, [selectedAddress, selectedDate]);

  useEffect(() => {
    if (!showNewAddress) return;
    if (!newAddress.state) {
      setCities([]);
      return;
    }
    fetchCitiesForState(newAddress.state);
  }, [newAddress.state, showNewAddress]);

  async function safeJson(res: Response, ctx: string) {
    try {
      return await res.json();
    } catch (err) {
      console.warn(`[Measurement] ${ctx} JSON parse failed`, err);
      const text = await res.text().catch(() => "");
      return { _text: text || "" };
    }
  }

  async function fetchInitialData() {
    setLoading(true);
    try {
      const addressesRes = await fetch(buildUrl("/api/customer/addresses"), { credentials: "same-origin" });
      const addressesData = await safeJson(addressesRes, "addresses");
      if (!addressesRes.ok) {
        toast.error(addressesData?.error || "Failed to load addresses");
      } else {
        const addrs = (addressesData as any).addresses || [];
        setAddresses(addrs);
        if (addrs.length > 0) {
          const def = addrs.find((a: Address) => a.isDefault) || addrs[0];
          setSelectedAddress(def);
        }
      }
    } catch (error) {
      console.error("[Measurement] fetchInitialData error", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  async function fetchCitiesForState(state: string) {
    setLoadingCities(true);
    const url = buildUrl(`/api/public/cities?state=${encodeURIComponent(state)}`);
    try {
      const res = await fetch(url, { credentials: "same-origin" });
      const data: any = await safeJson(res, "cities");
      if (!res.ok) {
        toast.error(data?.error || "Failed to load cities");
        setCities([]);
        return;
      }
      setCities((data as any).cities || []);
    } catch (err) {
      console.error("[Measurement] fetchCitiesForState error", err);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  }

  async function fetchSlots() {
    if (!selectedAddress || !selectedDate) return;

    setLoadingSlots(true);
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const url = buildUrl(
      `/api/public/slots?state=${encodeURIComponent(selectedAddress.state)}&city=${encodeURIComponent(
        selectedAddress.city
      )}&type=MEASUREMENT&date=${encodeURIComponent(dateStr)}`
    );

    try {
      const res = await fetch(url, { credentials: "same-origin" });
      const data: any = await safeJson(res, "slots");
      if (!res.ok) {
        toast.error(data?.error || "Failed to load available slots");
        setSlots([]);
        return;
      }
      setSlots((data as any).slots || []);
    } catch (error) {
      console.error("[Measurement] fetchSlots error", error);
      toast.error("Failed to load available slots");
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }

  function validateNewAddress() {
    const errors: string[] = [];
    if (!newAddress.state.trim()) errors.push("State is required");
    if (!newAddress.city.trim()) errors.push("City is required");
    if (!newAddress.line1.trim()) errors.push("Address line 1 is required");
    const pin = newAddress.pincode.trim();
    if (!pin || !/^\d{5,6}$/.test(pin)) errors.push("Valid pincode is required (5-6 digits)");
    return errors;
  }

  async function handleCreateAddress() {
    const errs = validateNewAddress();
    if (errs.length) {
      errs.forEach((e) => toast.error(e));
      return;
    }

    setCreatingAddress(true);
    const url = buildUrl("/api/customer/addresses");
    const payload = { ...newAddress };

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });
      const data: any = await safeJson(res, "createAddress");
      if (!res.ok) {
        toast.error(data?.error || "Failed to add address");
        return;
      }

      const created: Address = (data as any).address || (data as any);
      setAddresses((prev) => [created, ...prev]);
      setSelectedAddress(created);
      setShowNewAddress(false);
      setNewAddress({ line1: "", line2: "", city: "", state: "", country: "India", pincode: "" });
      toast.success("Address added");
    } catch (error) {
      console.error("[Measurement] createAddress error", error);
      toast.error("Failed to add address");
    } finally {
      setCreatingAddress(false);
    }
  }

  async function handleSubmit() {
    if (!selectedAddress || !selectedSlot) return;

    setSubmitting(true);
    const url = buildUrl("/api/customer/appointments");
    const payload = { addressId: selectedAddress.id, slotId: selectedSlot.id };

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });
      const data: any = await safeJson(res, "createAppointment");
      if (!res.ok) {
        toast.error(data?.error || "Failed to book appointment");
        return;
      }

      toast.success("Measurement visit booked successfully!");
      navigate(`/customer/appointments/${(data as any).appointmentId}`);
    } catch (error) {
      console.error("[Measurement] createAppointment error", error);
      toast.error("Failed to book appointment");
    } finally {
      setSubmitting(false);
    }
  }

  function canProceed(): boolean {
    switch (currentStep) {
      case 1:
        return !!selectedAddress;
      case 2:
        return !!selectedDate;
      case 3:
        return !!selectedSlot;
      default:
        return true;
    }
  }

  function handleNext() {
    if (currentStep < 4 && canProceed()) {
      setCurrentStep(currentStep + 1);
    }
  }

  function handleBack() {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }

  if (loading) {
    return (
      <div
        className="min-h-screen bg-background"
        style={{ paddingTop: "calc(var(--site-header-height, 96px) + env(safe-area-inset-top, 0px) + 16px)" }}
      >
        <main className="container mx-auto px-4 md:px-6 py-6">
          <PageHeader title="Book Measurement Visit" subtitle="Schedule a professional measurement session at your convenience" />
          <Skeleton className="h-[500px]" />
        </main>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-background"
      style={{ paddingTop: "calc(var(--site-header-height, 96px) + env(safe-area-inset-top, 0px) + 16px)" }}
    >
      <main className="container mx-auto px-4 md:px-6 py-6">
        <PageHeader title="Book Measurement Visit" subtitle="Schedule a professional measurement session at your convenience" />

        {/* Step headers */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <button
                onClick={() => step.id < currentStep && setCurrentStep(step.id)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-full transition-colors",
                  currentStep === step.id
                    ? "bg-primary text-primary-foreground"
                    : currentStep > step.id
                    ? "bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200"
                    : "bg-muted text-muted-foreground"
                )}
              >
                <span
                  className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold",
                    currentStep > step.id ? "bg-emerald-500 text-white" : "bg-background/20"
                  )}
                >
                  {currentStep > step.id ? <Check className="h-3 w-3" /> : step.id}
                </span>
                <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
              </button>
              {index < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-muted-foreground mx-2" />}
            </div>
          ))}
        </div>

        <Card className="max-w-2xl mx-auto">
          <CardContent className="pt-6">
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-semibold">Select Address</h3>
                </div>

                {addresses.length > 0 ? (
                  <div className="space-y-3">
                    {addresses.map((address) => (
                      <button
                        key={address.id}
                        onClick={() => setSelectedAddress(address)}
                        className={cn(
                          "w-full text-left p-4 rounded-lg border-2 transition-colors",
                          selectedAddress?.id === address.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                        )}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{address.line1}</p>
                            {address.line2 && <p className="text-sm text-muted-foreground">{address.line2}</p>}
                            <p className="text-sm text-muted-foreground">
                              {address.city}, {address.state} - {address.pincode}
                            </p>
                            {address.country && <p className="text-xs text-muted-foreground mt-1">Country: {address.country}</p>}
                          </div>
                          {selectedAddress?.id === address.id && <Check className="h-5 w-5 text-primary" />}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No addresses found.</p>
                  </div>
                )}

                <Button variant="outline" className="w-full" onClick={() => setShowNewAddress(!showNewAddress)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Address
                </Button>

                {showNewAddress && (
                  <div className="mt-2 p-4 rounded-lg border space-y-4">
                    <h4 className="font-semibold text-sm">New Address</h4>

                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <Input id="country" value={newAddress.country} readOnly />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>State</Label>
                        <Select
                          value={newAddress.state}
                          onValueChange={(val) => setNewAddress((s) => ({ ...s, state: val, city: "" }))}
                        >
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder="Select State" />
                          </SelectTrigger>
                          <SelectContent>
                            {INDIAN_STATES.map((s) => (
                              <SelectItem key={s} value={s}>
                                {s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>City</Label>
                        <Select
                          disabled={!newAddress.state || loadingCities}
                          value={newAddress.city}
                          onValueChange={(val) => setNewAddress((s) => ({ ...s, city: val }))}
                        >
                          <SelectTrigger className="h-10">
                            <SelectValue placeholder={loadingCities ? "Loading cities..." : "Select City"} />
                          </SelectTrigger>
                          <SelectContent>
                            {cities.map((c) => (
                              <SelectItem key={c.id} value={c.name}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="line1">Address Line 1</Label>
                      <Input
                        id="line1"
                        value={newAddress.line1}
                        onChange={(e) => setNewAddress((s) => ({ ...s, line1: e.target.value }))}
                        placeholder="House/Flat, Street"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="line2">Address Line 2 (optional)</Label>
                      <Input
                        id="line2"
                        value={newAddress.line2}
                        onChange={(e) => setNewAddress((s) => ({ ...s, line2: e.target.value }))}
                        placeholder="Landmark"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="pincode">Pincode</Label>
                        <Input
                          id="pincode"
                          value={newAddress.pincode}
                          onChange={(e) => setNewAddress((s) => ({ ...s, pincode: e.target.value }))}
                          placeholder="e.g. 560001"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="ghost" onClick={() => setShowNewAddress(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleCreateAddress} disabled={creatingAddress}>
                        {creatingAddress ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          "Save Address"
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-semibold">Select Date</h3>
                </div>

                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => date < new Date() || date < new Date("1900-01-01")}
                    className="rounded-md border"
                  />
                </div>

                {selectedDate && (
                  <p className="text-center text-sm text-muted-foreground mt-4">
                    Selected: {format(selectedDate, "EEEE, MMMM d, yyyy")}
                  </p>
                )}
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-semibold">Select Time Slot</h3>
                </div>

                {loadingSlots ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-16" />
                    ))}
                  </div>
                ) : slots.length > 0 ? (
                  <div className="space-y-3">
                    {slots.map((slot) => (
                      <button
                        key={slot.id}
                        onClick={() => setSelectedSlot(slot)}
                        className={cn(
                          "w-full text-left p-4 rounded-lg border-2 transition-colors",
                          selectedSlot?.id === slot.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{formatTimeRange(slot.startAt, slot.endAt)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {slot.capacityLeft} spots left
                            </Badge>
                            {selectedSlot?.id === slot.id && <Check className="h-5 w-5 text-primary" />}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No slots available for the selected date.</p>
                    <p className="text-sm mt-2">Please try a different date.</p>
                  </div>
                )}
              </div>
            )}

            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-semibold">Confirm Booking</h3>
                </div>

                <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Address</p>
                      <p className="font-medium">{selectedAddress?.line1}</p>
                      {selectedAddress?.line2 && <p className="text-sm">{selectedAddress.line2}</p>}
                      <p className="text-sm">
                        {selectedAddress?.city}, {selectedAddress?.state} - {selectedAddress?.pincode}
                      </p>
                      {selectedAddress?.country && <p className="text-sm">Country: {selectedAddress.country}</p>}
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CalendarIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-medium">{selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Time</p>
                      <p className="font-medium">{selectedSlot && formatTimeRange(selectedSlot.startAt, selectedSlot.endAt)}</p>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground text-center">
                  Our measurement executive will visit you at the scheduled time. You will receive an OTP for verification.
                </p>
              </div>
            )}

            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button variant="outline" onClick={currentStep === 1 ? () => navigate(-1) : handleBack}>
                <ChevronLeft className="h-4 w-4 mr-2" />
                {currentStep === 1 ? "Cancel" : "Back"}
              </Button>

              {currentStep < 4 ? (
                <Button onClick={handleNext} disabled={!canProceed()}>
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Booking...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Confirm Booking
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}